/**
 * Razorpay Checkout Utility
 * Creates order on backend, opens Razorpay checkout, and verifies payment.
 */
import api from '../api';

const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

let scriptLoaded = false;

function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (scriptLoaded || window.Razorpay) {
      scriptLoaded = true;
      return resolve();
    }
    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT_URL;
    script.onload = () => { scriptLoaded = true; resolve(); };
    script.onerror = () => reject(new Error('Failed to load Razorpay SDK'));
    document.body.appendChild(script);
  });
}

/**
 * Opens Razorpay Checkout with backend order creation and verification.
 * @param {Object} opts
 * @param {string} opts.userName - Prefill name
 * @param {string} opts.userEmail - Prefill email
 * @param {Function} opts.onSuccess - Called after verified payment
 * @param {Function} opts.onFailure - Called on payment failure/dismiss
 */
export async function openRazorpayCheckout({ userName = '', userEmail = '', onSuccess, onFailure }) {
  try {
    await loadRazorpayScript();
  } catch {
    if (onFailure) onFailure();
    return;
  }

  const keyId = process.env.REACT_APP_RAZORPAY_KEY_ID;
  if (!keyId || keyId.includes('PLACEHOLDER')) {
    if (onFailure) onFailure();
    return;
  }

  // Step 1: Create order on backend
  let order;
  try {
    const res = await api.post('/payment/create-order', { amount: 49900 });
    order = res.data;
  } catch (err) {
    if (onFailure) onFailure();
    return;
  }

  // Step 2: Open Razorpay checkout with order_id
  const options = {
    key: keyId,
    amount: order.amount,
    currency: order.currency,
    order_id: order.order_id,
    name: 'TestGenie AI',
    description: 'Pro Plan - Monthly Subscription',
    handler: async (response) => {
      // Step 3: Verify payment on backend
      try {
        const verifyRes = await api.post('/payment/verify', {
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_order_id: response.razorpay_order_id,
          razorpay_signature: response.razorpay_signature,
        });
        if (verifyRes.data.success && onSuccess) {
          onSuccess(verifyRes.data);
        }
      } catch {
        if (onFailure) onFailure();
      }
    },
    prefill: {
      name: userName,
      email: userEmail,
    },
    theme: {
      color: '#2563eb',
    },
    modal: {
      ondismiss: () => {
        if (onFailure) onFailure();
      },
    },
  };

  const rzp = new window.Razorpay(options);
  rzp.on('payment.failed', () => {
    if (onFailure) onFailure();
  });
  rzp.open();
}
